<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  funnel: {
    type: Object,
    default: null,
  },
});

const { t, locale } = useI18n();

const copy = computed(() => ({
  source: 'Shopify',
  title: t('SHOPIFY_FUNNEL.TITLE'),
  description: t('SHOPIFY_FUNNEL.DESCRIPTION'),
  catalogs: t('SHOPIFY_FUNNEL.CATALOGS'),
  selections: t('SHOPIFY_FUNNEL.SELECTIONS'),
  checkouts: t('SHOPIFY_FUNNEL.CHECKOUTS'),
  paid: t('SHOPIFY_FUNNEL.PAID'),
  fromPrevious: t('SHOPIFY_FUNNEL.FROM_PREVIOUS'),
  noData: t('SHOPIFY_FUNNEL.NO_DATA'),
  automaticReading: t('SHOPIFY_FUNNEL.AUTOMATIC_READING'),
  revenue: t('SHOPIFY_FUNNEL.REVENUE'),
  abandoned: t('SHOPIFY_FUNNEL.ABANDONED'),
}));

const totals = computed(() => props.funnel?.totals || {});
const stages = computed(() => [
  {
    key: 'catalogs_sent',
    label: copy.value.catalogs,
    value: Number(totals.value.catalogs_sent || 0),
  },
  {
    key: 'selections_received',
    label: copy.value.selections,
    value: Number(totals.value.selections_received || 0),
  },
  {
    key: 'checkout_links',
    label: copy.value.checkouts,
    value: Number(totals.value.checkout_links || 0),
  },
  {
    key: 'paid_orders',
    label: copy.value.paid,
    value: Number(totals.value.paid_orders || 0),
  },
]);
const hasData = computed(() => stages.value.some(stage => stage.value > 0));
const maxStageValue = computed(() =>
  Math.max(...stages.value.map(stage => stage.value), 1)
);
const percentage = (value, previous) => {
  if (!previous) return null;
  return Math.round((value / previous) * 100);
};
const stageRate = index =>
  index === 0
    ? null
    : percentage(stages.value[index].value, stages.value[index - 1].value);
const stageRateLabel = index => {
  const rate = stageRate(index);
  return rate === null ? '' : `${rate}% ${copy.value.fromPrevious}`;
};
const barWidth = value =>
  `${Math.max(value ? 16 : 0, Math.round((value / maxStageValue.value) * 100))}%`;
const revenue = computed(() =>
  new Intl.NumberFormat(String(locale.value).replace('_', '-'), {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(Number(totals.value.revenue || 0))
);
const reading = computed(() => {
  if (!hasData.value) return copy.value.noData;

  const transitions = stages.value
    .slice(1)
    .map((stage, index) => ({
      label: `${stages.value[index].label} → ${stage.label}`,
      rate: percentage(stage.value, stages.value[index].value),
    }))
    .filter(item => item.rate !== null);
  const bottleneck = transitions.sort((a, b) => a.rate - b.rate)[0];
  if (!bottleneck) return t('SHOPIFY_FUNNEL.INSUFFICIENT_TRANSITIONS');

  return t('SHOPIFY_FUNNEL.BOTTLENECK', {
    label: bottleneck.label,
    rate: bottleneck.rate,
  });
});
</script>

<template>
  <section
    class="mt-5 rounded-xl border border-blue-500/25 bg-blue-500/[0.04] p-4 sm:p-5"
  >
    <div
      class="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between"
    >
      <div>
        <p
          class="mb-1 text-xs font-medium uppercase tracking-wide text-blue-500"
        >
          {{ copy.source }}
        </p>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ copy.title }}
        </h3>
        <p class="mb-0 mt-1 text-sm text-n-slate-11">{{ copy.description }}</p>
      </div>
      <div class="mt-2 flex gap-4 text-right sm:mt-0">
        <div>
          <p class="mb-0 text-[11px] text-n-slate-10">{{ copy.revenue }}</p>
          <p class="mb-0 text-sm font-semibold text-n-slate-12">
            {{ revenue }}
          </p>
        </div>
        <div>
          <p class="mb-0 text-[11px] text-n-slate-10">{{ copy.abandoned }}</p>
          <p class="mb-0 text-sm font-semibold text-amber-500">
            {{ totals.abandoned_checkouts || 0 }}
          </p>
        </div>
      </div>
    </div>

    <p
      v-if="!hasData"
      class="mb-0 mt-4 rounded-lg bg-n-surface-1 p-3 text-sm text-n-slate-10"
    >
      {{ copy.noData }}
    </p>
    <div v-else class="mt-5 grid gap-3 lg:grid-cols-4">
      <article
        v-for="(stage, index) in stages"
        :key="stage.key"
        class="rounded-lg bg-n-surface-1 p-3"
      >
        <div class="flex items-start justify-between gap-2">
          <p class="mb-0 text-xs text-n-slate-10">{{ stage.label }}</p>
          <i
            v-if="index < stages.length - 1"
            class="i-lucide-arrow-right mt-0.5 text-sm text-blue-500"
          />
        </div>
        <p class="mb-0 mt-2 text-2xl font-semibold text-n-slate-12">
          {{ stage.value }}
        </p>
        <p class="mb-0 mt-1 h-4 text-xs text-n-slate-10">
          {{ stageRateLabel(index) }}
        </p>
        <div class="mt-3 h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
          <div
            class="h-full rounded-full bg-blue-500"
            :style="{ width: barWidth(stage.value) }"
          />
        </div>
      </article>
    </div>

    <div
      v-if="hasData"
      class="mt-4 flex gap-2 rounded-lg bg-n-surface-1 p-3 text-sm text-n-slate-11"
    >
      <i class="i-lucide-sparkles mt-0.5 shrink-0 text-blue-500" />
      <p class="mb-0">
        <strong class="text-n-slate-12">{{ copy.automaticReading }}</strong>
        {{ reading }}
      </p>
    </div>
  </section>
</template>
